import { PmlAllocVar, PmlComponent, PmlRecordType, PmlSimRecordType } from './types';

export const PML_MODEL_VERSION_2021 = '2021.1' as const;
export const PML_MODEL_VERSION_2025 = '2025.1' as const;

// --- Grids (spec §3.1) ------------------------------------------------------

export const REVENUE_GRID: number[] = [100_000_000, 5_000_000_000, 200_000_000_000];
export const RECORD_GRID: number[] = [
  10_000, 50_000, 100_000, 500_000, 1_000_000, 5_000_000, 10_000_000, 50_000_000, 100_000_000, 500_000_000,
  1_000_000_000,
];
export const MIN_RECORD_VAL = RECORD_GRID[0];
export const MAX_RECORD_VAL = RECORD_GRID[RECORD_GRID.length - 1];

// --- Record types (spec §3.2) ----------------------------------------------

/** PCI has no data_ind: it is not a simulated record type. */
export const DATA_IND: Record<PmlSimRecordType, number> = { PHI: 1, PFI: 2, PII: 3 };
export const SIM_RECORD_TYPES: PmlSimRecordType[] = ['PHI', 'PFI', 'PII'];
export const RECORD_TYPES: PmlRecordType[] = ['PHI', 'PFI', 'PII', 'PCI'];

// --- Cost components (spec §3.3) -------------------------------------------

export const ALLOC_VARS: PmlAllocVar[] = [
  'SIM_CIVIL_COST',
  'SIM_REGULATORY_FINES',
  'SIM_DEFENSE_COSTS',
  'BREACH_COACH_COST',
  'FORENSICS_COST',
  'PUBLIC_RELATIONS_COST',
  'NOTIFICATIONS_COST',
  'CREDIT_MONITORING_COST',
  'CALL_CENTER_COST',
];
export const BANK_CARD_REISS = 'BANK CARD_REISS' as const;
/** All ten components, in the order the totals are summed (matches the reference implementation). */
export const PML_COMPONENTS: PmlComponent[] = [...ALLOC_VARS, BANK_CARD_REISS];

// --- Curve table columns (spec §6.2, §6.4) ---------------------------------

export const CURVE_COLUMNS = {
  BI_SEVERITY: 'BI_SEV_QUANTILE',
  RW_SEVERITY: 'RW_SEV_QUANTILE',
  FORENSICS: 'forensics_quantile',
  BREACH_COACH: 'breachcoach_quantile',
  PR: 'pr_quantile',
} as const;
export const REQUIRED_CURVE_COLUMNS: string[] = Object.keys(CURVE_COLUMNS).map(k => CURVE_COLUMNS[k]);

// --- Ransomware severity parameters (spec §6.4) ----------------------------

export const EXT_PARAMS = {
  INTERCEPT: 'Intercept',
  LN_REVENUE: 'LN_REVENUE',
  DISPERSION: 'Dispersion',
  POWER: 'Power',
  IND_HEALTH: 'LN_REVENU*IND_HEALTH',
  IND_EDUCAT: 'LN_REVENU*IND_EDUCAT',
} as const;
export const REQUIRED_EXT_PARAMS: string[] = [
  EXT_PARAMS.INTERCEPT,
  EXT_PARAMS.LN_REVENUE,
  EXT_PARAMS.DISPERSION,
  EXT_PARAMS.POWER,
];
export const RANSOMWARE_PERCENTILE = 0.75;

// --- BI defaults and constants (spec §6.1, §7.1) ---------------------------

export const DEFAULT_DOWNTIME_DAYS = 60;
export const DEFAULT_PCT_IMPACTED = 1;
export const DAYS_PER_YEAR = 365;
/** Linear recovery ramp. Replaces v1's 60-row downtime decay table and `daySum = 9.14`. */
export const RECOVERY_RAMP = 0.5;

// --- Initial impact % default (spec §6.3, corrected in Addendum B §B1) -----

export const IMPACT_BAND_LO: number[] = [0, 1e8, 5e8, 1e9, 5e9, 1e10, 5e10, 1e11, 5e11];
export const IMPACT_BAND_HI: number[] = [...IMPACT_BAND_LO.slice(1), 999_999_999_999];
export const IMPACT_PCT_LO: number[] = [1.0, 1.0, 0.9, 0.65, 0.3, 0.2, 0.1, 0.08, 0.04];
export const IMPACT_PCT_HI: number[] = [1.0, 0.9, 0.65, 0.3, 0.25, 0.1, 0.08, 0.04, 0.04];
export const IMPACT_FLOOR = 0.3;

// --- Table integrity (spec §9.3) -------------------------------------------

export const EXPECTED_ROWS = { total: 99, allocations: 891, bcr: 11, curve: 9999 } as const;
export const ALLOCATION_SUM_TOLERANCE = 1e-9;

// --- Runtime table location (Addendum C) -----------------------------------

/** Table version the browser loads when no manifest says otherwise. A corrected table set is a NEW version. */
export const PML_DEFAULT_TABLE_VERSION = 'V1_5';
/** S3 key prefix (Amplify Storage `public/` level) holding one folder per table version. */
export const PML_TABLES_S3_PREFIX = 'public/BNB_TEMPLATES/PML';
/** File names inside `<prefix>/<version>/derived/`. `manifest.json` is optional. */
export const PML_TABLE_FILES = {
  total: 'sim_outputs_total.json',
  allocations: 'sim_outputs_allocations.json',
  bcr: 'sim_outputs_bcr.json',
  ext: 'extnsevest_pml.json',
  curve: 'complexity_curve_dists.csv',
  manifest: 'manifest.json',
} as const;
